
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface SilverPackageOrder {
  id: string;
  name: string;
  email: string;
  paymentDate: string;
  originalAmount: number;
  couponCode: string | null;
  paidAmount: number;
  status: string;
  orderId: string;
}

export const useSilverPackageOrders = (
  page: number = 1, 
  pageSize: number = 30, 
  searchQuery: string = '',
  sortOrder: 'asc' | 'desc' = 'desc'
) => {
  const [orders, setOrders] = useState<SilverPackageOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        
        // Calculate pagination
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        
        // 1. Get total count of silver package orders
        const { count: totalItems, error: countError } = await supabase
          .from('orders')
          .select('*', { count: 'exact' })
          .eq('package_type', 'silver');
        
        if (countError) {
          throw new Error(`Error counting orders: ${countError.message}`);
        }
        
        if (totalItems !== null) {
          setTotalCount(totalItems);
        }
        
        // 2. Fetch silver package orders with pagination and sorting
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .eq('package_type', 'silver')
          .order('created_at', { ascending: sortOrder === 'asc' })
          .range(from, to);
        
        if (ordersError) {
          throw new Error(`Error fetching orders: ${ordersError.message}`);
        }
        
        if (!ordersData || ordersData.length === 0) {
          setOrders([]);
          setLoading(false);
          return;
        }
        
        // 3. Get the unique user IDs from the orders
        const userIds = [...new Set(ordersData.map(order => order.user_id))];
        
        // 4. Fetch profiles for these user IDs
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, name, email')
          .in('id', userIds);
        
        if (profilesError) {
          throw new Error(`Error fetching user profiles: ${profilesError.message}`);
        }
        
        // Create a map of user_id to profile data for easier lookup
        const profilesMap = profilesData?.reduce((acc, profile) => {
          acc[profile.id] = profile;
          return acc;
        }, {} as Record<string, any>) || {};
        
        // 5. Combine and format the data
        const formattedOrders = ordersData
          .map(order => {
            const profile = profilesMap[order.user_id];
            
            // Apply search filter if provided
            if (
              searchQuery && 
              profile && 
              !profile.name?.toLowerCase().includes(searchQuery.toLowerCase()) && 
              !profile.email?.toLowerCase().includes(searchQuery.toLowerCase())
            ) {
              return null;
            }
            
            return {
              id: order.id,
              orderId: order.id,
              name: profile?.name || 'Unknown',
              email: profile?.email || 'Unknown',
              paymentDate: new Date(order.created_at).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              }),
              originalAmount: order.original_amount,
              couponCode: order.coupon_code,
              paidAmount: order.amount,
              status: order.status
            };
          })
          .filter(Boolean) as SilverPackageOrder[];
        
        setOrders(formattedOrders);
      } catch (err: any) {
        setError(err.message);
        toast.error(`Failed to load orders: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [page, pageSize, searchQuery, sortOrder]);

  return { orders, loading, error, totalCount };
};
